import { useState, type FC } from "react";
import BrandLogo from "../../components/BrandLogo";
import { Button } from "../../components/ui/button";
import { gluceraAssessmentCta } from "../../utils/gluceraTypography";
import {ChevronUp, ChevronDown, Plus, X, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";

interface DoseEntry {
  id: string;
  name: string;
  frequency: string;
  time: string;
}

const AddMedications: FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"medicines" | "insulin">(
    "medicines"
  );

  // Handle form submission with error handling
  const handleAddMedication = () => {
    // Validation check
    if (activeTab === "medicines") {
      if (!medicineName.trim() || !isValidMedicineName(medicineName)) {
        return;
      }
      if (doses.length === 0) {
        return;
      }
    } else {
      if (!isInsulinFormValid()) {
        return;
      }
    }

    let medicationData;

    if (activeTab === "medicines") {
      // Save medicine data with simplified structure
      const simplifiedDoses = doses.map(dose => ({
        frequency: dose.frequency,
        time: dose.time,
      }));

      medicationData = {
        id: Date.now(), // Add unique ID
        name: medicineName,
        doses: simplifiedDoses,
        type: "medicine", // Add type identifier
      };
    } else {
      // Save insulin data
      medicationData = {
        id: Date.now(), // Simple ID generation
        doseType,
        insulinType,
        timing,
        dose,
        brand,
        type: "insulin",
      };
    }

    try {
      // Get existing medications from localStorage
      const existingMedications = JSON.parse(
        localStorage.getItem("userMedications") ?? "[]"
      );

      // Add new medication
      existingMedications.push(medicationData);

      // Save back to localStorage
      localStorage.setItem(
        "userMedications",
        JSON.stringify(existingMedications)
      );

      // Reset form fields
      if (activeTab === "medicines") {
        setMedicineName("");
        setDoses([]);
        setNewDose({ name: "", frequency: "", time: "" });
      } else {
        setDoseType("");
        setInsulinType("");
        setTiming("");
        setDose("");
        setBrand("");
      }

      // Redirect back to current medications page
      navigate("/glucera" + GluceraNavigationRoutes.CURRENT_MEDICATIONS);
    } catch (error) {
      // Error handled silently
    }
  };

  // Medicines form state
  const [medicineName, setMedicineName] = useState<string>("");

  // Time picker state (for dose modal)
  const [showTimePicker, setShowTimePicker] = useState<boolean>(false);
  const [selectedTime, setSelectedTime] = useState({
    hour: 0,
    minute: 0,
  });

  // Multiple doses state
  const [doses, setDoses] = useState<DoseEntry[]>([]);
  const [showAddDoseModal, setShowAddDoseModal] = useState<boolean>(false);
  const [newDose, setNewDose] = useState({
    name: "",
    frequency: "",
    time: "",
  });

  // Insulin form state
  const [doseType, setDoseType] = useState<string>("");
  const [insulinType, setInsulinType] = useState<string>("");
  const [timing, setTiming] = useState<string>("");
  const [dose, setDose] = useState<string>("");
  const [brand, setBrand] = useState<string>("");

  // Form validation functions
  const isValidMedicineName = (name: string) => {
    // Allow letters, numbers, spaces, hyphens, brackets, and common medication characters
    return (
      name.trim().length >= 2 && /^[a-zA-Z0-9\s\-().+]+$/.test(name.trim())
    );
  };

  const isValidTime = (timeStr: string) => {
    // Check for common time formats: HH:MM AM/PM or HH:MM (24-hour)
    const timeRegex =
      /^(0?[1-9]|1[0-2]):[0-5][0-9]\s?(AM|PM|am|pm)$|^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(timeStr.trim());
  };

  const isValidFrequency = (freq: string) => {
    // Check for common frequencies
    const frequencyRegex =
      /^(once|twice|thrice|\d+\s?times?)(\s?(daily|weekly|monthly|per day|a day))?$/i;
    return frequencyRegex.test(freq.trim()) || freq.trim().length >= 3;
  };

  const isMedicinesFormValid = () => {
    return (
      isValidMedicineName(medicineName) &&
      doses.length > 0 &&
      doses.every(
        dose => isValidFrequency(dose.frequency) && isValidTime(dose.time)
      )
    );
  };

  const isValidBrand = (brandStr: string) => {
    return (
      brandStr.trim().length >= 2 && /^[a-zA-Z0-9\s-]+$/.test(brandStr.trim())
    );
  };

  const isValidInsulinDose = (doseStr: string) => {
    // For dropdown selection, just check if it's not empty and is a valid number
    const trimmed = doseStr.trim();
    return trimmed !== "" && !isNaN(parseInt(trimmed));
  };

  const isInsulinFormValid = () => {
    return (
      doseType !== "" &&
      insulinType !== "" &&
      timing !== "" &&
      isValidInsulinDose(dose) &&
      isValidBrand(brand)
    );
  };

  const isFormValid =
    activeTab === "medicines" ? isMedicinesFormValid() : isInsulinFormValid();

  // Time picker handlers (removed unused function)

  const handleSaveTime = () => {
    const formattedTime = `${String(selectedTime.hour).padStart(2, "0")}:${String(selectedTime.minute).padStart(2, "0")}`;
    setNewDose({ ...newDose, time: formattedTime });
    setShowTimePicker(false);
  };

  // Multiple doses functions
  const addDose = () => {
    if (newDose.frequency && newDose.time) {
      const dose: DoseEntry = {
        id: Date.now().toString(),
        name: `Dose ${doses.length + 1}`,
        frequency: newDose.frequency,
        time: newDose.time,
      };
      setDoses([...doses, dose]);
      setNewDose({ name: "", frequency: "", time: "" });
      setShowAddDoseModal(false);
    }
  };

  const removeDose = (id: string) => {
    setDoses(doses.filter(dose => dose.id !== id));
  };

  const openAddDoseModal = () => {
    setNewDose({
      name: `Dose ${doses.length + 1}`,
      frequency: "",
      time: "",
    });
    setShowAddDoseModal(true);
  };

  // Dropdown options
  const doseTypeOptions = ["Fixed", "Sliding Scale", "Mixed"];
  const insulinTypeOptions = [
    "Rapid-Acting",
    "Short-Acting",
    "Intermediate-Acting",
    "Long-Acting",
  ];
  const timingOptions = [
    "Before Breakfast",
    "After Breakfast",
    "Before Lunch",
    "After Lunch",
    "Before Dinner",
    "After Dinner",
    "Bedtime",
  ];

  return (
    <div className="h-screen w-screen overflow-hidden bg-background">
      <div className="mx-auto flex h-full w-full max-w-md flex-col">
        {/* Header */}
        <div className="space-y-4 px-4 pb-4 pt-safe">
          <BrandLogo />

          {/* Assessment Progress */}
          <p className="text-center text-sm text-muted-foreground">
            Assessment 6 of 11
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="mb-6 space-y-2">
            <h1 className="text-3xl font-bold text-foreground sm:text-4xl">
              {activeTab === "medicines"
                ? "Add Your Medications"
                : "Add Your Current Insulin"}
            </h1>
          </div>

          {/* Tabs */}
          <div className="mb-6 flex overflow-hidden rounded-xl">
            <button
              className={`flex-1 py-3 text-base font-semibold transition-all ${
                activeTab === "medicines"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
              onClick={() => setActiveTab("medicines")}
            >
              Add Medicines
            </button>
            <button
              className={`flex-1 py-3 text-base font-semibold transition-all ${
                activeTab === "insulin"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
              onClick={() => setActiveTab("insulin")}
            >
              Add Insulin
            </button>
          </div>

          {/* Form Fields */}
          {activeTab === "medicines" ? (
            <div className="space-y-6">
              {/* Medication Name - First Field */}
              <div>
                <label className="mb-2 block text-sm font-semibold text-foreground">
                  Medication Name *
                </label>
                <input
                  type="text"
                  placeholder="Enter medication name"
                  value={medicineName}
                  onChange={e => setMedicineName(e.target.value)}
                  className={`w-full rounded-xl border-2 bg-card px-4 py-3 text-base text-foreground focus:outline-none focus:ring-2 ${
                    medicineName && !isValidMedicineName(medicineName)
                      ? "border-destructive focus:border-destructive focus:ring-destructive/20"
                      : "border-input focus:border-primary focus:ring-primary/20"
                  }`}
                />
                {medicineName && !isValidMedicineName(medicineName) && (
                  <div className="mt-1 text-xs text-destructive">
                    Please enter a valid medicine name (at least 2 letters)
                  </div>
                )}
              </div>

              {/* Daily Doses Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-foreground">
                    Daily Doses *
                  </h3>
                  <Button
                    type="button"
                    onClick={openAddDoseModal}
                    className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-white hover:bg-primary/90"
                  >
                    <Plus className="h-4 w-4" />
                    Add Dose
                  </Button>
                </div>

                {/* Doses List */}
                {doses.map(dose => (
                  <div
                    key={dose.id}
                    className="rounded-lg border-2 border-border bg-card p-4"
                  >
                    <div className="mb-2 flex items-start justify-between">
                      <h4 className="font-semibold text-foreground">
                        {dose.name}
                      </h4>
                      <Button
                        onClick={() => removeDose(dose.id)}
                        variant="ghost"
                        size="sm"
                        className="h-auto p-1 text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium">Frequency:</span>{" "}
                        {dose.frequency}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium">Time:</span> {dose.time}
                      </p>
                    </div>
                  </div>
                ))}

                {doses.length === 0 && (
                  <div className="rounded-lg border-2 border-dashed border-border bg-muted/20 p-6 text-center">
                    <p className="text-muted-foreground">No doses added yet</p>
                    <p className="text-sm text-muted-foreground">
                      Click "Add Dose" to get started
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-semibold text-foreground">
                  Dose Type
                </label>
                <select
                  value={doseType}
                  onChange={e => setDoseType(e.target.value)}
                  className="w-full appearance-none rounded-xl border-2 border-input bg-card px-4 py-3 text-base text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">Select Dose Type</option>
                  {doseTypeOptions.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-foreground">
                  Insulin Type
                </label>
                <select
                  value={insulinType}
                  onChange={e => setInsulinType(e.target.value)}
                  className="w-full appearance-none rounded-xl border-2 border-input bg-card px-4 py-3 text-base text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">Select Insulin Type</option>
                  {insulinTypeOptions.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-foreground">
                  Timing
                </label>
                <select
                  value={timing}
                  onChange={e => setTiming(e.target.value)}
                  className="w-full appearance-none rounded-xl border-2 border-input bg-card px-4 py-3 text-base text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">Select Timing</option>
                  {timingOptions.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-foreground">
                  Dose
                </label>
                <select
                  value={dose}
                  onChange={e => setDose(e.target.value)}
                  className="w-full appearance-none rounded-xl border-2 border-input bg-card px-4 py-3 text-base text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">Select Dose</option>
                  {Array.from({ length: 100 }, (_, i) => i + 1).map(num => (
                    <option key={num} value={num.toString()}>
                      {num} {num === 1 ? "unit" : "units"}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-foreground">
                  Brand
                </label>
                <input
                  type="text"
                  placeholder="Lantus, Humalog"
                  value={brand}
                  onChange={e => setBrand(e.target.value)}
                  className={`w-full rounded-xl border-2 bg-card px-4 py-3 text-base text-foreground focus:outline-none focus:ring-2 ${
                    brand && !isValidBrand(brand)
                      ? "border-destructive focus:border-destructive focus:ring-destructive/20"
                      : "border-input focus:border-primary focus:ring-primary/20"
                  }`}
                />
                {brand && !isValidBrand(brand) && (
                  <div className="mt-1 text-xs text-destructive">
                    Please enter a valid brand name (at least 2 characters)
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Button */}
        <div className="pb-safe border-t p-4 space-y-3">
          <Button
            onClick={handleAddMedication}
            disabled={!isFormValid}
            className={gluceraAssessmentCta}
          >
            Add {activeTab === "medicines" ? "Medicine" : "Insulin"}
          </Button>
          <p
            className="cursor-pointer text-center text-sm text-muted-foreground hover:text-foreground"
            onClick={() => navigate("/glucera" + GluceraNavigationRoutes.WEIGHT_LOSS)}
          >
            Skip
          </p>
        </div>

        {/* Custom Time Picker Modal */}
        {showTimePicker && (
          <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/50"
            onClick={() => setShowTimePicker(false)}
          >
            <div className="w-full max-w-md" onClick={e => e.stopPropagation()}>
              <div className="rounded-t-3xl bg-background p-6">
                {/* Header */}
                <h3 className="mb-8 text-left text-lg font-semibold text-foreground">
                  Set time
                </h3>

                {/* Selected Time Display */}
                <div className="mb-6 flex items-center justify-center gap-3 text-4xl font-medium text-primary">
                  <span>{String(selectedTime.hour).padStart(2, "0")}</span>
                  <span>:</span>
                  <span>{String(selectedTime.minute).padStart(2, "0")}</span>
                </div>

                {/* Interactive Picker */}
                <div className="mb-8 flex items-center justify-center gap-12">
                  {/* Hour Column */}
                  <div className="flex flex-col items-center">
                    <button
                      onClick={() =>
                        setSelectedTime(prev => ({
                          ...prev,
                          hour: prev.hour === 23 ? 0 : prev.hour + 1,
                        }))
                      }
                      className="mb-4 flex h-8 w-8 items-center justify-center text-muted-foreground hover:text-foreground"
                    >
                      <ChevronUp className="h-6 w-6" />
                    </button>
                    <div className="flex h-12 w-16 items-center justify-center rounded-lg border-2 border-input bg-card text-2xl font-medium text-foreground">
                      {String(selectedTime.hour).padStart(2, "0")}
                    </div>
                    <button
                      onClick={() =>
                        setSelectedTime(prev => ({
                          ...prev,
                          hour: prev.hour === 0 ? 23 : prev.hour - 1,
                        }))
                      }
                      className="mt-4 flex h-8 w-8 items-center justify-center text-muted-foreground hover:text-foreground"
                    >
                      <ChevronDown className="h-6 w-6" />
                    </button>
                  </div>

                  {/* Minute Column */}
                  <div className="flex flex-col items-center">
                    <button
                      onClick={() =>
                        setSelectedTime(prev => ({
                          ...prev,
                          minute: prev.minute === 59 ? 0 : prev.minute + 1,
                        }))
                      }
                      className="mb-4 flex h-8 w-8 items-center justify-center text-muted-foreground hover:text-foreground"
                    >
                      <ChevronUp className="h-6 w-6" />
                    </button>
                    <div className="flex h-12 w-16 items-center justify-center rounded-lg border-2 border-input bg-card text-2xl font-medium text-foreground">
                      {String(selectedTime.minute).padStart(2, "0")}
                    </div>
                    <button
                      onClick={() =>
                        setSelectedTime(prev => ({
                          ...prev,
                          minute: prev.minute === 0 ? 59 : prev.minute - 1,
                        }))
                      }
                      className="mt-4 flex h-8 w-8 items-center justify-center text-muted-foreground hover:text-foreground"
                    >
                      <ChevronDown className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Bottom Row - 24 Hour Time Display */}
                <div className="mb-8 flex items-center justify-center gap-8 text-xl tracking-wider text-muted-foreground">
                  <span>{String(selectedTime.hour).padStart(2, "0")}</span>
                  <span>:</span>
                  <span>{String(selectedTime.minute).padStart(2, "0")}</span>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4">
                  <Button
                    onClick={() => setShowTimePicker(false)}
                    variant="outline"
                    className="h-12 flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveTime}
                    className="h-12 flex-1 bg-primary hover:bg-primary/90"
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Dose Modal */}
        {showAddDoseModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">
                  Add Dose
                </h3>
                <Button
                  onClick={() => setShowAddDoseModal(false)}
                  variant="ghost"
                  size="sm"
                  className="h-auto p-1"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    Frequency *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., With breakfast, Before meals"
                    value={newDose.frequency}
                    onChange={e =>
                      setNewDose({ ...newDose, frequency: e.target.value })
                    }
                    className="w-full rounded-lg border-2 border-input bg-background px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    Time *
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
                    <input
                      type="time"
                      value={newDose.time}
                      onChange={e =>
                        setNewDose({ ...newDose, time: e.target.value })
                      }
                      className="w-full rounded-lg border-2 border-input bg-background px-10 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
                    />
                  </div>
                </div>

                <div className="mt-6 flex gap-2">
                  <Button
                    onClick={() => setShowAddDoseModal(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={addDose}
                    className="flex-1"
                    disabled={!newDose.frequency || !newDose.time}
                  >
                    Add Dose
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddMedications;
